// Validator-recovery modal.
//
// When an `api.createIssue` POST returns a 400 with `errorMessages`, the
// quick-create flow can't satisfy a workflow validator. This modal opens,
// parses the field names out of the validator messages, fetches the
// schema for each via createmeta, and asks the user to fill them in.
// On submit it retries the create. If the retry triggers a fresh set of
// validator errors (chained validators), the new fields are appended and
// the user is asked again.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api, { extractErrorMessage } from '@app/services/api';
import SearchableSelect from '@platform/ui/SearchableSelect';
import IssuePickerInput from '@modules/database/work-items/frontend/components/IssuePickerInput';
import '@modules/database/work-items/frontend/components/ValidatorRecoveryModal/ValidatorRecoveryModal.css';
import {
  schemaMapFromCreateMeta, parseValidatorMessages, buildFillableFromMissing,
  issuePickerKind, issuePickerValue,
} from '@modules/database/work-items/frontend/components/ValidatorRecoveryModal/validatorRecoveryFields';

// Map a UI value to the JSON shape Jira's create endpoint expects for
// this schema.
function toJiraShape(fieldId, schema, value) {
  if (value === null || value === undefined || value === '') return undefined;
  const pickKind = issuePickerKind(fieldId, schema);
  if (pickKind) return issuePickerValue(pickKind, value);
  const t = schema?.type;
  const items = schema?.items;
  const sys = schema?.system;

  if (t === 'string') return String(value);
  if (t === 'number') {
    const n = Number(value);
    return isNaN(n) ? undefined : n;
  }
  if (t === 'date') return value;       // 'YYYY-MM-DD' string
  if (t === 'datetime') return value;   // ISO string from datetime-local
  if (t === 'option') {
    return value?.id ? { id: value.id } : (typeof value === 'string' ? { id: value } : undefined);
  }
  if (t === 'priority' || sys === 'priority' || t === 'resolution' || sys === 'resolution') {
    return value?.id ? { id: value.id } : (typeof value === 'string' ? { id: value } : undefined);
  }
  if (t === 'user' || sys === 'assignee' || sys === 'reporter') {
    if (value?.accountId) return { accountId: value.accountId };
    return undefined;
  }
  if (t === 'array') {
    if (items === 'option') {
      const arr = Array.isArray(value) ? value : [value];
      return arr.filter(Boolean).map(v => v?.id ? { id: v.id } : (typeof v === 'string' ? { id: v } : null)).filter(Boolean);
    }
    if (items === 'user') {
      const arr = Array.isArray(value) ? value : [value];
      return arr.filter(Boolean).map(v => v?.accountId ? { accountId: v.accountId } : null).filter(Boolean);
    }
    if (items === 'string') {
      // Labels
      if (Array.isArray(value)) return value;
      return String(value).split(',').map(s => s.trim()).filter(Boolean);
    }
    if (items === 'version' || items === 'component') {
      const arr = Array.isArray(value) ? value : [value];
      return arr.filter(Boolean).map(v => v?.id ? { id: v.id } : null).filter(Boolean);
    }
  }
  // Unknown / unhandled type → send raw.
  return value;
}

// One input row per field. Lifted from the BulkEditModal pattern so the
// UX matches what users see elsewhere in the PWA.
function FieldInput({ fieldId, schema, value, onChange, disabled, projectKey, issueTypeId }) {
  const t = schema?.type;
  const items = schema?.items;
  const sys = schema?.system;
  const allowedValues = Array.isArray(schema?.allowedValues) ? schema.allowedValues : null;

  // Local state for user search.
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [extraOptions, setExtraOptions] = useState(null);

  // For system priority/resolution we pull the global list when the
  // schema didn't give us allowedValues.
  useEffect(() => {
    let cancelled = false;
    if (allowedValues) return;
    const wantsSystemList =
      sys === 'priority' || t === 'priority' ||
      sys === 'resolution' || t === 'resolution';
    if (!wantsSystemList) return;
    const loader = (sys === 'priority' || t === 'priority')
      ? api.getPriorities()
      : api.getResolutions();
    loader.then(list => {
      if (!cancelled) setExtraOptions((list || []).map(o => ({ id: o.id, value: o.name })));
    }).catch(() => { if (!cancelled) setExtraOptions([]); });
    return () => { cancelled = true; };
  }, [sys, t, allowedValues]);

  // Debounced user search.
  const isUser = t === 'user' || sys === 'assignee' || sys === 'reporter' ||
                 (t === 'array' && items === 'user');
  useEffect(() => {
    if (!isUser) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setUserLoading(true);
      try {
        // A custom user field searches only its context's allowed users.
        const scope = String(fieldId).startsWith('customfield_') ? { fieldId, projectKey, issueTypeId } : null;
        const list = await api.getUsers(userQuery, scope);
        if (!cancelled) setUserResults(Array.isArray(list) ? list.slice(0, 20) : []);
      } catch { if (!cancelled) setUserResults([]); }
      finally { if (!cancelled) setUserLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isUser, userQuery, fieldId, projectKey, issueTypeId]);

  const options = allowedValues || extraOptions;

  // Parent / Epic Link: pick the work item it points at.
  if (issuePickerKind(fieldId, schema)) {
    return (
      <div className="vrm-issue-picker">
        <IssuePickerInput
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          onSelect={key => onChange(key)}
          project={projectKey}
          disabled={disabled}
          placeholder="Search work items…"
        />
      </div>
    );
  }

  // Render by schema.
  if (isUser) {
    const isMulti = t === 'array';
    const list = isMulti ? (Array.isArray(value) ? value : []) : null;
    return (
      <div className="vrm-field-control">
        {isMulti && list.length > 0 && (
          <div className="vrm-chip-row">
            {list.map(u => (
              <span key={u.accountId} className="vrm-chip">
                {u.displayName || u.emailAddress || u.accountId}
                <button type="button" className="vrm-chip-clear"
                  onClick={() => onChange(list.filter(x => x.accountId !== u.accountId))}
                  disabled={disabled}>×</button>
              </span>
            ))}
          </div>
        )}
        {(!isMulti && value) ? (
          <div className="vrm-chip-row">
            <span className="vrm-chip">
              {value.displayName || value.emailAddress || value.accountId}
              <button type="button" className="vrm-chip-clear"
                onClick={() => onChange(null)} disabled={disabled}>×</button>
            </span>
          </div>
        ) : (
          <>
            <input type="text" className="vrm-input"
              placeholder="Search by name or email…"
              value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              disabled={disabled} />
            {(userQuery || userResults.length > 0) && (
              <div className="vrm-user-results">
                {userLoading && <div className="vrm-user-state">Searching…</div>}
                {!userLoading && userResults.length === 0 && userQuery && (
                  <div className="vrm-user-state">No matches</div>
                )}
                {userResults.map(u => (
                  <button key={u.accountId} type="button" className="vrm-user-item"
                    onClick={() => {
                      if (isMulti) {
                        const next = [...(list || [])];
                        if (!next.some(x => x.accountId === u.accountId)) next.push(u);
                        onChange(next);
                      } else {
                        onChange(u);
                      }
                      setUserQuery('');
                      setUserResults([]);
                    }}
                    disabled={disabled}>
                    {u.displayName || u.emailAddress || u.accountId}
                    {u.emailAddress && <span className="vrm-user-email">{u.emailAddress}</span>}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (t === 'date' || t === 'datetime') {
    // Open the native picker on focus/click (not just the calendar icon)
    // so the whole field is the click target. showPicker() throws if the
    // input is hidden or already programmatically opened — swallow.
    const openPicker = (e) => {
      try { e.currentTarget.showPicker?.(); } catch { /* no-op */ }
    };
    return (
      <input type={t === 'date' ? 'date' : 'datetime-local'} className="vrm-input"
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        onFocus={openPicker}
        onClick={openPicker}
        disabled={disabled} />
    );
  }

  if (t === 'number') {
    return (
      <input type="number" className="vrm-input"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={disabled} />
    );
  }

  // Single-option select (system or custom)
  if (t === 'option' || t === 'priority' || sys === 'priority' || t === 'resolution' || sys === 'resolution') {
    if (options == null) return <div className="vrm-input-hint">Loading options…</div>;
    if (options.length === 0) return <div className="vrm-input-hint">No options available.</div>;
    return (
      <SearchableSelect
        options={options.map(o => ({ id: o.id, name: o.value || o.name || String(o.id) }))}
        value={value?.id || ''}
        onChange={(val) => {
          if (!val) { onChange(null); return; }
          const o = options.find(x => String(x.id) === String(val));
          onChange(o ? { id: o.id, value: o.value || o.name } : null);
        }}
        disabled={disabled}
        placeholder="Search options..."
        noneLabel="— Pick a value —"
      />
    );
  }

  // Multi-select
  if (t === 'array' && items === 'option') {
    if (options == null) return <div className="vrm-input-hint">Loading options…</div>;
    if (options.length === 0) return <div className="vrm-input-hint">No options available.</div>;
    const list = Array.isArray(value) ? value : [];
    return (
      <div className="vrm-field-control">
        {list.length > 0 && (
          <div className="vrm-chip-row">
            {list.map(o => (
              <span key={o.id} className="vrm-chip">
                {o.value || o.name || String(o.id)}
                <button type="button" className="vrm-chip-clear"
                  onClick={() => onChange(list.filter(x => x.id !== o.id))}
                  disabled={disabled}>×</button>
              </span>
            ))}
          </div>
        )}
        <SearchableSelect
          options={options.filter(o => !list.some(x => x.id === o.id))
                          .map(o => ({ id: o.id, name: o.value || o.name || String(o.id) }))}
          value=""
          onChange={(val) => {
            if (!val) return;
            const o = options.find(x => String(x.id) === String(val));
            if (o && !list.some(x => x.id === o.id)) {
              onChange([...list, { id: o.id, value: o.value || o.name }]);
            }
          }}
          disabled={disabled}
          placeholder="Search options..."
          noneLabel="— Add a value —"
        />
      </div>
    );
  }

  // Labels (array<string>)
  if (t === 'array' && items === 'string') {
    return (
      <div>
        <input type="text" className="vrm-input"
          value={Array.isArray(value) ? value.join(', ') : (value || '')}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          placeholder="comma,separated,labels" />
        <div className="vrm-input-hint-small">Comma-separated.</div>
      </div>
    );
  }

  // Versions / components (array of objects)
  if (t === 'array' && (items === 'version' || items === 'component')) {
    if (options == null) return (
      <div>
        <input type="text" className="vrm-input"
          value={Array.isArray(value) ? value.map(v => v.name).join(', ') : ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          placeholder={`${items} names, comma-separated`} />
        <div className="vrm-input-hint-small">No options list returned by Jira — type names directly.</div>
      </div>
    );
    return (
      <SearchableSelect
        options={options.map(o => ({ id: o.id, name: o.value || o.name || String(o.id) }))}
        value={(Array.isArray(value) && value[0]?.id) || ''}
        onChange={(val) => {
          if (!val) { onChange([]); return; }
          const o = options.find(x => String(x.id) === String(val));
          onChange(o ? [{ id: o.id, name: o.value || o.name }] : []);
        }}
        disabled={disabled}
        placeholder="Search options..."
        noneLabel="— Pick a value —"
      />
    );
  }

  // Default: plain text
  return (
    <input type="text" className="vrm-input"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      disabled={disabled} />
  );
}

// Main modal. Stays mounted across chained-validator retries: when a
// retry returns fresh "must be filled in" errors, the matching schema
// rows are appended in place.
function ValidatorRecoveryModal({
  isOpen,
  originalFields,
  errorMessages,
  missingFields,
  projectKey,
  issuetypeId,
  issuetypeName,
  onSubmitted,
  onCancel,
}) {
  const [resolvedTypeId, setResolvedTypeId] = useState(issuetypeId || null);
  const [schemas, setSchemas] = useState(null);
  const [parsed, setParsed] = useState({ fillable: [], unparseable: [] });
  const [formValues, setFormValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Jira screen-layout field positions, keyed by fieldId. Fields not in
  // the layout are pushed to the end of the rendered list. Built once on
  // open and reused for chained-validator appends.
  const [fieldOrderIndex, setFieldOrderIndex] = useState(null);
  const overlayRef = useRef(null);
  // Global field registry (fieldId -> api.getFields row), fetched once on
  // open. Used to resolve required fields that aren't on the create screen.
  const registryRef = useRef({});

  // Reset on open and load schemas.
  useEffect(() => {
    if (!isOpen) return;
    setFormValues({});
    setError(null);
    setLoading(true);

    (async () => {
      try {
        // Resolve issuetype id if we only got a name.
        let typeId = issuetypeId || resolvedTypeId;
        if (!typeId && issuetypeName) {
          const types = await api.getProjectIssueTypes(projectKey);
          const match = (types || []).find(t => t.name === issuetypeName);
          if (!match) throw new Error(`Could not resolve work type "${issuetypeName}"`);
          typeId = match.id;
        }
        if (!typeId) throw new Error('Missing work type id');
        setResolvedTypeId(typeId);

        // Load schemas + (best-effort) Jira screen layout in parallel.
        // The screen layout gives us the field positions Jira itself uses
        // on the create screen — we sort the fillable rows by it so the
        // modal matches what users see in Jira. Failure is silent: we
        // fall back to validator-message order.
        const [meta, projects, allFields] = await Promise.all([
          api.getCreateMeta(projectKey, typeId),
          api.getProjects().catch(() => []),
          api.getFields().catch(() => []),
        ]);
        const projId = (projects || []).find(p => p.key === projectKey)?.id;
        const screen = projId
          ? await api.getScreenLayout(projId, typeId, 'create', true).catch(() => null)
          : null;
        if (screen?.tabs?.length > 0) {
          const idx = {};
          let pos = 0;
          for (const tab of screen.tabs) {
            for (const f of (tab.fields || [])) {
              if (f.id != null && idx[f.id] === undefined) idx[f.id] = pos++;
            }
          }
          setFieldOrderIndex(idx);
        } else {
          setFieldOrderIndex({});
        }
        // fieldId -> flat schema, so FieldInput resolves the true
        // type/items/allowedValues. /api/createmeta is an OBJECT keyed by
        // fieldId; the helper also accepts the internal array form.
        const schemaMap = schemaMapFromCreateMeta(meta);
        setSchemas(schemaMap);

        // Global field registry — resolves required fields that aren't on
        // the create screen (validator-required but off-screen).
        const registryMap = {};
        for (const f of (Array.isArray(allFields) ? allFields : [])) {
          if (f?.id) registryMap[f.id] = f;
        }
        registryRef.current = registryMap;

        // Prefer the create-gate's deduped missingFields to build the input
        // rows directly. Fall back to the legacy message-text regex only
        // when missingFields is absent.
        const hasMissing = Array.isArray(missingFields) && missingFields.length > 0;
        setParsed(hasMissing
          ? { fillable: buildFillableFromMissing(missingFields, schemaMap, registryMap), unparseable: [] }
          : parseValidatorMessages(errorMessages || [], schemaMap, registryMap));
      } catch (err) {
        setError(extractErrorMessage(err, 'Failed to load create metadata'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === overlayRef.current && !submitting) onCancel();
  }, [onCancel, submitting]);

  const updateField = useCallback((fieldId, value) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Render-time sort: stable sort `fillable` by the Jira screen position
  // we built on open. Fields missing from the layout (rare — happens for
  // certain custom validators that emit names not present on the screen)
  // sort to the end while keeping their relative order from the validator
  // message array.
  const sortedFillable = useMemo(() => {
    if (!fieldOrderIndex) return parsed.fillable;
    const arr = parsed.fillable.map((f, i) => ({ f, i }));
    arr.sort((a, b) => {
      const pa = fieldOrderIndex[a.f.fieldId];
      const pb = fieldOrderIndex[b.f.fieldId];
      const aPresent = pa !== undefined;
      const bPresent = pb !== undefined;
      if (aPresent && bPresent) return pa - pb;
      if (aPresent) return -1;
      if (bPresent) return 1;
      return a.i - b.i;
    });
    return arr.map(x => x.f);
  }, [parsed.fillable, fieldOrderIndex]);

  // Append fields from a fresh round of validator messages. Used when a
  // retry triggers chained validators we hadn't seen yet.
  const appendNewFields = useCallback((newMessages, newMissing) => {
    setParsed(prev => {
      const seen = new Set(prev.fillable.map(f => f.fieldId));
      const newFillable = [];
      // Prefer the retry's deduped missingFields (id-based, no regex).
      if (Array.isArray(newMissing) && newMissing.length > 0) {
        for (const f of buildFillableFromMissing(newMissing, schemas || {}, registryRef.current || {})) {
          if (!seen.has(f.fieldId)) { newFillable.push(f); seen.add(f.fieldId); }
        }
      }
      // Legacy regex fallback for any messages not covered by missingFields.
      const fresh = parseValidatorMessages(newMessages || [], schemas || {}, registryRef.current || {});
      for (const f of fresh.fillable) {
        if (!seen.has(f.fieldId)) { newFillable.push(f); seen.add(f.fieldId); }
      }
      // Replace unparseable wholesale so old generic messages don't pile up.
      return {
        fillable: [...prev.fillable, ...newFillable],
        unparseable: fresh.unparseable,
      };
    });
  }, [schemas]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    // Build the merged payload.
    const merged = { ...originalFields };
    for (const f of parsed.fillable) {
      const raw = formValues[f.fieldId];
      const shaped = toJiraShape(f.fieldId, f.schema, raw);
      if (shaped !== undefined) merged[f.fieldId] = shaped;
    }

    try {
      const created = await api.createIssue({ fields: merged });
      // Modal owner runs the post-create logic and closes us.
      await onSubmitted(created, merged);
    } catch (err) {
      const details = err.response?.data?.details;
      const newMissing = Array.isArray(details?.missingFields) ? details.missingFields : null;
      const msgs = Array.isArray(details?.errorMessages) ? details.errorMessages : null;
      if ((newMissing && newMissing.length > 0) || (msgs && msgs.length > 0)) {
        // Chained validators or still missing — append the new rows.
        appendNewFields(msgs, newMissing);
        setError('Some fields still need attention — please review and submit again.');
      } else {
        setError(extractErrorMessage(err, 'Create failed'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [originalFields, formValues, parsed.fillable, onSubmitted, appendNewFields]);

  if (!isOpen) return null;

  // Portaled to <body>, like the create screen it recovers: rendered in
  // place it sat inside its caller's stacking context (a grid's sticky
  // pane) and was drawn UNDER the create screen it belongs to.
  return createPortal(
    <div className="vrm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="vrm-modal" onClick={e => e.stopPropagation()}>
        <div className="vrm-header">
          <div>
            <div className="vrm-title">The following fields need to be fill in</div>
          </div>
          <button type="button" className="vrm-close" onClick={onCancel} disabled={submitting} aria-label="Cancel">×</button>
        </div>

        {loading && <div className="vrm-loading">Loading field schemas…</div>}

        {!loading && parsed.unparseable.length > 0 && (
          <div className="vrm-banner vrm-banner-info">
            <div className="vrm-banner-title">Validator messages</div>
            <ul className="vrm-banner-list">
              {parsed.unparseable.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {!loading && parsed.fillable.length === 0 && parsed.unparseable.length === 0 && (
          <div className="vrm-banner vrm-banner-info">
            No fillable validator fields could be parsed. Check the work item in Jira to satisfy the workflow validator.
          </div>
        )}

        {!loading && sortedFillable.length > 0 && (
          <div className="vrm-fields">
            {sortedFillable.map(f => (
              <div key={f.fieldId} className="vrm-field">
                <label className="vrm-field-label">
                  {f.schema?.name || f.fieldId}
                </label>
                <FieldInput
                  fieldId={f.fieldId}
                  schema={f.schema}
                  projectKey={projectKey}
                  issueTypeId={issuetypeId || resolvedTypeId}
                  value={formValues[f.fieldId]}
                  onChange={(v) => updateField(f.fieldId, v)}
                  disabled={submitting}
                />
              </div>
            ))}
          </div>
        )}

        {error && <div className="vrm-error">{error}</div>}

        <div className="vrm-actions">
          <button type="button" className="vrm-btn vrm-btn-secondary"
            onClick={onCancel} disabled={submitting}>Cancel</button>
          {(loading || parsed.fillable.length > 0) && (
            <button type="button" className="vrm-btn vrm-btn-primary"
              onClick={handleSubmit}
              disabled={submitting || loading}>
              {submitting ? 'Creating…' : 'Create work item'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ValidatorRecoveryModal;
